import { decorationBox, useDecorationFit } from "../lib/decoration";
import { markFor, type MarkKind, type Person } from "../lib/people";
import { initials, tint } from "@shared/lib/tint";
import { FileImage } from "@shared/ui/FileImage";

export function Mark({ kind, size = 10, on }: { kind: MarkKind; size?: number; on?: string }) {
  return (
    <span
      className={`mark mark-${kind}`}
      style={
        {
          width: size,
          height: size,
          "--mark-on": on,
          "--mark-ring": `${Math.max(2, size * 0.22)}px`,
        } as React.CSSProperties
      }
      aria-hidden
    />
  );
}

interface TileProps {
  person: Person;
  size?: number;
  /** Скругление. Круг по умолчанию; квадратное берут те, кому круг мал. */
  radius?: number | string;
  /**
   * Метка присутствия: `null` убирает её, значение задаёт вручную, пропуск
   * оставляет ту, что следует из самого человека.
   */
  mark?: MarkKind | null;
}

/** С какого размера на плашке видно украшение: на мелкой (упоминание, реакция) — каша. */
const DECORATION_FROM = 28;

/**
 * Плашка человека: инициалы или аватар в одной и той же коробке.
 *
 * Раскладка не знает, что внутри — поэтому список, где у половины людей
 * картинка есть, а у половины нет, не разъезжается.
 */
export function Tile({ person, size = 32, radius, mark }: TileProps) {
  const kind = mark === undefined ? markFor(person) : mark;
  const decoration = size >= DECORATION_FROM ? person.decorationId : null;
  const fit = useDecorationFit(decoration);
  const box = fit && decorationBox(fit, size);

  return (
    <span className="tile">
      <span
        className="tile-box"
        style={{
          width: size,
          height: size,
          borderRadius: radius ?? "50%",
          fontSize: Math.round(size * 0.4),
          background: tint(person.handle || person.name),
        }}
      >
        {person.avatarId ? (
          <FileImage id={person.avatarId} alt="" width={size} height={size} />
        ) : (
          initials(person.name)
        )}
      </span>
      {/*
        Украшение — поверх аватара, но под меткой присутствия; место плашки
        оно не занимает. Где на картинке аватар, говорит мера в самом файле
        (`decoration.ts`): так шляпа может торчать вверх, а не обрезаться
        квадратом. Без меры — квадрат на пятую часть больше, как в Discord.
      */}
      {decoration && box && (
        <span className="tile-decor" style={box} aria-hidden>
          <FileImage id={decoration} alt="" width={Math.round(box.width)} height={Math.round(box.height)} />
        </span>
      )}
      {kind && (
        <span className="tile-mark">
          <Mark kind={kind} size={Math.max(9, Math.round(size * 0.34))} />
        </span>
      )}
    </span>
  );
}
